<template>
  <v-card outlined elevation="0" class="rounded-lg mt-4" tile>
    <v-sheet :class="showCard ? '' : 'rounded-lg'" color="third">
      <v-card-title class="third white--text font-weight-bold">
        <v-btn icon @click="showCard = !showCard">
          <v-icon color="white">{{ showCard ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </v-btn>
        <span class="mr-4">Commandes Loisirs</span>
        <v-select
          v-if="showCard"
          v-model="statusFilter"
          class="col-2"
          placeholder="Statut"
          :items="['All', 'Paid', 'Not Paid']"
          hide-details
          dense
          solo
          flat
        />
        <v-spacer />
        <v-btn elevation="0" icon @click="searchOrders()">
          <v-icon color="white" class="pr-1">mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>
    </v-sheet>
    <v-expand-transition>
      <div v-show="showCard" class="grey lighten-4">
        <v-divider />
        <v-data-table
          :headers="visibleHeaders"
          fixed-header
          height="300"
          :items="tableItems"
          :server-items-length="search.results.count"
          :loading="search.loading ? 'third' : false"
          :options.sync="options"
          loading-text="Chargement... Veuillez patienter"
          class="elevation-1"
          @update:page="searchOrders()"
          @update:items-per-page="searchOrders()"
        >
          <template #item.principalProduct="{ item }">
            {{ principalProduct(item) }}
          </template>

          <template #item.totalPrice="{ item }">
            {{ `${totalPrice(item)} €` }}
          </template>

          <template #item.isPaid="{ item }">
            <a v-if="item.order.orderUrl" :href="item.order.orderUrl" class="link-btn" target="_blank">
              {{ item.paymentStatus || (item.isPaid ? 'Paid' : 'Not paid') }}
            </a>
            <span v-else>{{ item.paymentStatus || (item.isPaid ? 'Paid' : 'Not paid') }}</span>
          </template>

          <template #item.orderStock="{ item }">
            <div v-if="orderStock(item) !== null">
              <span>{{ orderStock(item) }}</span>
              <v-icon class="ml-1" size="large" @click="openStockModal(item)">mdi-pencil</v-icon>
            </div>
          </template>

          <template #item.validityEnd="{ item }">
            <div v-if="orderValidityEnd(item)">
              {{ orderValidityEnd(item) }}
              <v-icon v-if="canShowEditContractDate" class="ml-1" size="large" @click="openContractDateModal(item)">mdi-pencil</v-icon>
            </div>
          </template>

          <template #item.isBuyer="{ item }">
            {{ item.buyer.uuid === customer.uuid ? 'Oui' : 'Non' }}
          </template>

          <template #item.holder="{ item }">
            <div v-for="(contact, index) in item.holder" :key="`holder_${index}`">
              <span>{{ contact.firstname }} {{ contact.lastname }}</span>
              <a :href="`/customer/${contact.uuid}`" target="_blank" class="ml-1"><v-icon>mdi-open-in-new</v-icon></a>
            </div>
          </template>

          <template #item.qrcode="{ item }">
            {{ (item.order.order_items.reduce((prev, curr) => curr.qrcode && curr.qrcode.length ? [...prev, ...curr.qrcode] : prev, []) || []).join(', ') }}
          </template>
        </v-data-table>
      </div>
    </v-expand-transition>

    <UpdateStockModal
      :show="showStockModal"
      :order="selectedOrder"
      :customer="customer"
      :fetched-orders="search.results.items || []"
      :on-close="closeStockModal"
      :on-stock-updated="updateStock"
    />

    <ContractDateModal
      :show="showContractDateModal"
      :order="selectedOrder"
      :customer="customer"
      :on-close="closeContractDateModal"
      :on-validity-updated="updateContractValidity"
    />
  </v-card>
</template>

<script>
import { ref, computed, watch, onMounted } from '@vue/composition-api';
import { format, isValid } from 'date-fns';
import { getOrders } from '@/helpers/request';
import store from '@/store';
import UpdateStockModal from './UpdateStockModal.vue';
import ContractDateModal from './ContractDateModal.vue';

export default {
  components: { UpdateStockModal, ContractDateModal },
  props: {
    customer: { type: Object, required: true }
  },
  setup (props) {
    const search = ref({ abortController: null, loading: false, results: {} });
    const options = ref({ itemsPerPage: 5 });
    const showCard = ref(true);
    const statusFilter = ref('All');
    const selectedOrder = ref(null);
    const showStockModal = ref(false);
    const showContractDateModal = ref(false);

    const headers = computed(() => [
      { text: 'Nº Commande', value: 'uuid', sortable: false, width: '300px', align: 'center' },
      { text: 'Produit principal', value: 'principalProduct', sortable: false, width: '200px', align: 'center' },
      { text: 'Total', value: 'totalPrice', sortable: false, width: '100px', align: 'center' },
      { text: 'Statut', value: 'isPaid', sortable: false, width: '100px', align: 'center' },
      { text: 'Crédit', value: 'orderStock', sortable: false, width: '75px', align: 'center', hidden: !tableItems.value.some(item => item.order.isInternalSession) },
      { text: 'Date', value: 'date', sortable: false, width: '125px', align: 'center' },
      { text: "Date d'expiration", value: 'validityEnd', sortable: false, width: '150px', align: 'center', hidden: !tableItems.value.some(item => item.order.isInternalSession) },
      { text: 'Payeur', value: 'isBuyer', sortable: false, width: '75px', align: 'center' },
      { text: 'Titulaire', value: 'holder', sortable: false, width: '160px', align: 'center' },
      { text: 'N° Support', value: 'qrcode', sortable: false, width: '150px', align: 'center' }
    ]);

    const visibleHeaders = computed(() => headers.value.filter(h => !h.hidden));

    const tableItems = computed(() => {
      if (search.value.results && search.value.results.items) {
        return search.value.results.items.map(order => ({
          ...order,
          holder: order.order && order.order.order_items ? order.order.order_items.map(o => o.customer) : null
        }));
      }
      return [];
    });

    const canShowEditContractDate = computed(() => {
      return (store.state.user.roles || []).some(role => ['admin', 'alpha_contract_end_date_update'].includes(role));
    });

    const searchOrders = async () => {
      const { page } = options.value;
      const itemsPerPage = options.value.itemsPerPage === -1 ? search.value.results.count : options.value.itemsPerPage;
      search.value.loading = true;

      if (search.value.abortController) {
        search.value.abortController.abort();
      }
      search.value.abortController = new AbortController();

      const start = (page - 1) * itemsPerPage || 0;
      const response = await getOrders(start, itemsPerPage, props.customer.uuid, 'alpha', statusFilter.value);

      if (response.success) {
        search.value.results = response.data;
      }
      search.value.loading = false;
    };

    const totalPrice = (genericOrder) => (genericOrder.order.total / 100).toFixed(2);

    const principalProduct = (genericOrder) => {
      const orderItem = genericOrder.order.order_items[0];
      return `${orderItem.product_name} - ${orderItem.horanet_product_name}`;
    };

    const orderStock = (order) => {
      const customerOrderItem = order.order.order_items.find(oi => oi.customer.uuid === props.customer.uuid && oi.reloadable);
      return customerOrderItem && customerOrderItem.contract ? customerOrderItem.contract.remainingStock : null;
    };

    const orderValidityEnd = (order) => {
      const customerOrderItem = order.order.order_items.find(oi => oi.customer.uuid === props.customer.uuid);
      if (!customerOrderItem || !customerOrderItem.contract || !customerOrderItem.contract.validityEnd) return null;
      const validityEnd = new Date(customerOrderItem.contract.validityEnd.split('T')[0]);
      if (!isValid(validityEnd)) return null;
      return format(validityEnd, 'dd/MM/yyyy');
    };

    const openStockModal = (item) => {
      selectedOrder.value = item;
      showStockModal.value = true;
    };

    const closeStockModal = () => {
      showStockModal.value = false;
    };

    const openContractDateModal = (item) => {
      selectedOrder.value = item;
      showContractDateModal.value = true;
    };

    const closeContractDateModal = () => {
      showContractDateModal.value = false;
    };

    const updateStock = ({ contractNumber, newStock, remainingStockMinusReservations }) => {
      const updatedItems = JSON.parse(JSON.stringify(search.value.results.items));
      updatedItems.forEach(order => {
        order.order.order_items.forEach(item => {
          if (item.contract && item.contract.contractNumber === contractNumber) {
            item.contract.remainingStock = newStock;
            item.contract.remainingStockMinusReservations = remainingStockMinusReservations;
            selectedOrder.value = order;
          }
        });
      });
      search.value.results.items = updatedItems;
    };

    const updateContractValidity = ({ contractNumber, validityEnd }) => {
      const updatedItems = JSON.parse(JSON.stringify(search.value.results.items));
      updatedItems.forEach(order => {
        order.order.order_items.forEach(item => {
          if (item.contract && item.contract.contractNumber === contractNumber) {
            item.contract.validityEnd = validityEnd;
          }
        });
      });
      search.value.results.items = updatedItems;
    };

    watch(statusFilter, () => searchOrders());

    onMounted(() => searchOrders());

    return {
      search, 
      options, 
      showCard, 
      statusFilter, 
      selectedOrder,
      showStockModal, 
      showContractDateModal,
      visibleHeaders, 
      tableItems, 
      canShowEditContractDate,
      searchOrders, 
      totalPrice, 
      principalProduct, 
      orderStock, 
      orderValidityEnd,
      openStockModal, 
      closeStockModal, 
      openContractDateModal, 
      closeContractDateModal,
      updateStock, 
      updateContractValidity
    };
  }
};
</script>

<style scoped>
.link-btn {
  text-decoration: none;
  color: blue;
  cursor: pointer;
}
</style>
